import { useState } from 'react';

import {
  Box,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { useQueryClient } from '@tanstack/react-query';

import Button from '@/components/button';
import Dialog from '@/components/dialog/Dialog';
import {
  useFetchCreatorsAutocomplete,
  useMergeCreators
} from '@/hooks/queries/creators';
import useAlert from '@/hooks/useAlert';
import { Creator } from '@/models/creator';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selected: GridRowSelectionModel;
  clearSelection: () => void;
  creatorsList: Array<Creator.Item>;
}

const MergeCreators = ({
  selected,
  isOpen,
  onClose,
  clearSelection,
  creatorsList
}: Props) => {
  const alert = useAlert();
  const queryClient = useQueryClient();

  const [mainCreatorId, setMainCreatorId] = useState<number | null>(null);

  const { data } = useFetchCreatorsAutocomplete({
    params: {
      groups: false
    }
  });

  const { mutate: mergeCreators, isPending: isSubmitting } = useMergeCreators({
    id: Number(mainCreatorId),
    onSuccess: () => {
      alert.success(`Creators successfully merged!`);
      queryClient.invalidateQueries({
        queryKey: ['creators'],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creatorGroups'],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creatorGroup'],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creator', Number(mainCreatorId)],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creatorsAutocomplete'],
        refetchType: 'all'
      });
      clearSelection();
      setMainCreatorId(null);
      onClose();
    },
    onError: () =>
      alert.error(`Some error occurred while merging the creators.`)
  });

  const handleSelectCreator = (event: SelectChangeEvent<number>) => {
    setMainCreatorId((event.target.value as number) ?? null);
  };

  const handleSubmit = () => {
    if (mainCreatorId === null) return;

    const creatorIds = (selected as number[]).filter(
      (id) => id !== mainCreatorId
    );

    mergeCreators({ ids: creatorIds });
  };

  const creatorsOptions =
    data?.creators?.filter((creator) => selected.includes(creator.id)) ?? [];

  const remainingCreators = creatorsList.filter(
    (creator) => selected.includes(creator.id) && creator.id !== mainCreatorId
  );

  const count = remainingCreators.length;
  const label = count === 1 ? 'creator' : 'creators';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Merge Creators"
      fullWidth
      dialogContent={
        <Stack gap={1}>
          <Box marginBottom={1}>
            <Typography>{`Are you sure you want to merge ${count} ${label}?`}</Typography>
            <Typography color="error.main">
              This action is not reversible.
            </Typography>
          </Box>
          <FormControl fullWidth>
            <InputLabel id="merge-into-label">Merge into</InputLabel>
            <Select
              label="Merge into"
              value={mainCreatorId ?? ''}
              onChange={handleSelectCreator}
            >
              {creatorsOptions.map((creator) => (
                <MenuItem key={creator.id} value={creator.id}>
                  <Typography>{creator.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box border="1px solid gray">
            <List
              sx={{ maxHeight: 360, overflowY: 'auto' }}
              dense
              disablePadding
            >
              {remainingCreators.map((creator) => (
                <ListItem
                  key={creator.id}
                  disableGutters
                  sx={{
                    px: 2,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&::before': {
                      content: '"•"',
                      color: 'text.secondary',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      lineHeight: 1
                    }
                  }}
                >
                  <ListItemText
                    primary={creator.name}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      }
      actions={
        <>
          <Button variant="text" text="Cancel" onClick={onClose} />
          <Button
            onClick={handleSubmit}
            text="Merge"
            loading={isSubmitting}
            disabled={mainCreatorId === null}
          />
        </>
      }
    />
  );
};

export default MergeCreators;
